import { useMemo } from 'react';
import Typography, { TypographyProps } from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';
import { AbiItem } from 'web3-utils';

interface Props extends Omit<TypographyProps, 'value'> {
  value?: string | AbiItem;
  dataTid?: string;
}

interface Params {
  name: string;
  type: string;
}

interface SignatureParams {
  name: string;
  params: Params[];
}

const functionNameRegexp = /((?:function )[a-zA-Z_{1}][a-zA-Z0-9_]+)(?=\()/gm;
const paramsRegexp = /\(\s*([^)]+?)\s*\)/gm;

export function Signature({ value, dataTid, ...props }: Props): JSX.Element {
  const { classes } = useStyles();

  const { name, params }: SignatureParams = useMemo(() => {
    if (typeof value !== 'string') {
      return {
        name: value?.name ?? '',
        params: value?.inputs ?? [],
      };
    }

    const functionName = (value.match(functionNameRegexp)?.[0] ?? '').replace(
      'function ',
      '',
    );
    const paramsStr = (paramsRegexp.exec(value)?.[1] ?? '').split(',');

    const params: Params[] = [];

    paramsStr.forEach(value => {
      const matchedValues = value.match(/\S*/gm);
      if (!matchedValues) {
        return;
      }

      const [name, type] = matchedValues.filter(Boolean);
      if (!name || !type) {
        return;
      }

      params.push({ name, type });
    });

    return {
      name: functionName,
      params,
    };
  }, [value]);

  return (
    <Typography
      data-tid={dataTid}
      variant="body2"
      color={theme => theme.palette.grey[900]}
      {...props}
    >
      <span className={classes.name}>{name}</span>(
      {params?.map(({ name, type }, i) => (
        <>
          {i !== 0 && ', '}
          <span className={classes.parameter}>{type}</span>
          {name && ` ${name}`}
        </>
      ))}
      )
    </Typography>
  );
}

const useStyles = makeStyles()(theme => ({
  name: {
    color: theme.palette.primary.main,
  },
  parameter: {
    color: theme.palette.success.main,
  },
}));
